import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X, Calendar as CalendarIcon, CheckSquare, Palmtree } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Searchable input that filters events, tasks and vacations across all views.
 * Selecting a result navigates the calendar to that date and opens the event.
 */
const CalendarSearch = ({ events = [], taskEvents = [], vacations = [], users = [], setCurrentDate, setViewMode, openEditEventModal }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const userMap = useMemo(() => {
        const m = {};
        users.forEach(u => { m[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim(); });
        return m;
    }, [users]);

    const results = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (term.length < 2) return [];
        const out = [];

        events.forEach(e => {
            const hay = `${e.title || ''} ${e.description || ''} ${e.location || ''}`.toLowerCase();
            if (hay.includes(term)) {
                out.push({
                    type: 'event',
                    id: e.id,
                    title: e.title,
                    date: e.start,
                    color: e.color,
                    raw: e,
                });
            }
        });

        taskEvents.forEach(t => {
            const hay = `${t.title || ''} ${t.description || ''}`.toLowerCase();
            if (hay.includes(term)) {
                out.push({
                    type: 'task',
                    id: t.id,
                    title: t.title,
                    date: t.start,
                    color: t.color,
                    raw: t,
                });
            }
        });

        vacations.forEach(v => {
            const userName = userMap[v.user_id] || '';
            const hay = `urlaub ${userName} ${v.reason || ''}`.toLowerCase();
            if (hay.includes(term)) {
                out.push({
                    type: 'vacation',
                    id: v.id,
                    title: `Urlaub: ${userName}`,
                    date: v.start_date,
                    color: '#34c759',
                    raw: v,
                });
            }
        });

        // Sort by date (closest to today first)
        const today = Date.now();
        out.sort((a, b) => {
            const da = a.date ? Math.abs(new Date(a.date).getTime() - today) : Infinity;
            const dbb = b.date ? Math.abs(new Date(b.date).getTime() - today) : Infinity;
            return da - dbb;
        });

        return out.slice(0, 30);
    }, [query, events, taskEvents, vacations, userMap]);

    const handleSelect = (r) => {
        if (r.date) {
            try {
                const d = typeof r.date === 'string' ? parseISO(r.date) : new Date(r.date);
                setCurrentDate(d);
                setViewMode('day');
            } catch { /* ignore */ }
        }
        if (r.type !== 'vacation' && openEditEventModal) {
            try { openEditEventModal(r.raw); } catch { /* ignore */ }
        }
        setOpen(false);
        setQuery('');
    };

    const TypeIcon = ({ type }) => {
        if (type === 'task') return <CheckSquare className="w-3.5 h-3.5" />;
        if (type === 'vacation') return <Palmtree className="w-3.5 h-3.5" />;
        return <CalendarIcon className="w-3.5 h-3.5" />;
    };

    return (
        <div ref={wrapperRef} className="relative mb-3" data-testid="calendar-search">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Termine, Tasks, Urlaube durchsuchen..."
                    className="w-full pl-9 pr-9 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="calendar-search-input"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(''); setOpen(false); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Suche zurücksetzen"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {open && query.length >= 2 && (
                <div className="absolute z-20 mt-1 left-0 right-0 rounded-xl border border-border bg-background shadow-xl max-h-[400px] overflow-y-auto" data-testid="calendar-search-results">
                    {results.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-muted-foreground text-center">Keine Treffer für "{query}"</p>
                    ) : (
                        <ul className="py-1">
                            {results.map((r) => (
                                <li key={`${r.type}-${r.id}`}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(r)}
                                        className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors flex items-center gap-2.5"
                                        data-testid={`calendar-search-result-${r.id}`}
                                    >
                                        <span
                                            className="w-7 h-7 rounded-md flex items-center justify-center text-white flex-shrink-0"
                                            style={{ backgroundColor: r.color || '#5856D6' }}
                                        >
                                            <TypeIcon type={r.type} />
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-medium truncate">{r.title}</span>
                                            <span className="block text-xs text-muted-foreground">
                                                {r.date ? format(typeof r.date === 'string' ? parseISO(r.date) : new Date(r.date), 'EEE, dd. MMM yyyy', { locale: de }) : ''}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default CalendarSearch;
